import * as React from 'react';
import { useState, useCallback, useEffect, useRef } from 'react';
import * as R from 'ramda';
import { css } from '@emotion/core';
import { Str } from '../../../localization/translationKeys';
import { FLEX_COLUMN } from '../../../layout/flexBox';
import { useMsgText } from '../../../localization/hooks';
import { COLORS } from '../../../globalStyle/colors';
import { Map } from '../../../language/models';
import { DevPlanFieldAndAnswer } from '../models';
import { useDispatch } from 'react-redux';
import { makeUpdateFieldAnswerRequestObj } from './module';
import { updateDevPlanFieldAnswerAction } from './api/updateDevPlanFieldAnswerAction';
import { useAppState } from '../../../core/redux/hooks';
import { keyFor } from '../../../core/api/module';
import { ActionType } from '../../../core/redux/actionType';
import DevActionListField from '../listItems/DevActionListField';
import DevActionField from '../DevActionField';
import { TEXT_AREA } from '../../../forms/styles/textarea';


const SAVE_DEBOUNCE_MS = 700;


const styles = {
  root:        css`
    transition: opacity .5s ease;
  `,
  loadingForm: css`
    opacity: .7;
  `,
  inputGroup:  [
    FLEX_COLUMN,
    css`
      align-items: stretch;
      margin-bottom: 35px;
    `,
  ],
  textArea:    css`
    width: 100%;
    background-color: ${COLORS.WHITE};
    font-size: 13px;
    ::placeholder {
      font-style: italic;
    }
  `,
};


/**
 * Returns the number of new lines in the given maybe string. If the maybe string isn't a string, then you get back 1.
 */
const calcLines = R.memoizeWith(
  (s: string | undefined): string => `${s}`,
  (s: string | undefined): number => s?.split('\n')?.length ?? 1,
);


type Props = {
  fieldsAnswers: DevPlanFieldAndAnswer[];
  devItemCompetencyId?: number;
  isParticipant: boolean;
  className?: string;
};


const fieldKey   = (field: DevPlanFieldAndAnswer): string => `${field.devPlanFieldId}`;
const loadingKey = (field: DevPlanFieldAndAnswer): string => keyFor(
  ActionType.UpdateDevPlanFieldAnswerRequest,
  `${field.devActionId}-${field.devPlanFieldId}`,
);


/**
 * DevActionForm: renders a list of dev plan question/answer fields. Each field's text area is always editable for
 * participants; edits are auto-saved on a per-field debounce while typing and immediately on blur.
 */
const DevActionForm: React.FC<Props> = props => {
  const dispatch    = useDispatch();
  const placeholder = useMsgText(Str.ResponseNotAdded);

  const [values, setValues] = useState<Map<string>>({});

  // Per-field bookkeeping held in refs so stable callbacks always read fresh state.
  const valuesRef     = useRef<Map<string>>(values);
  const fieldsRef     = useRef<DevPlanFieldAndAnswer[]>(props.fieldsAnswers);
  const loadingRef    = useRef<Map<boolean>>({});
  const wasLoadingRef = useRef<Map<boolean>>({});
  // The value we most recently dispatched (or accepted from the server) for each field. Skip re-saves when desired ===
  // lastSaved; trigger a follow-up save when desired diverged during an in-flight save.
  const lastSavedRef  = useRef<Map<string>>({});
  const timeoutsRef   = useRef<{ [key: string]: ReturnType<typeof setTimeout> }>({});

  useEffect(() => { valuesRef.current = values; },              [values]);
  useEffect(() => { fieldsRef.current = props.fieldsAnswers; }, [props.fieldsAnswers]);

  // Per-field loading state derived from redux. Each field has its own loading key, so saving one field doesn't gate
  // others.
  const loadingByField = useAppState(state => {
    const m: { [key: string]: boolean } = {};
    props.fieldsAnswers.forEach(field => {
      m[fieldKey(field)] = !!state.loading[loadingKey(field)];
    });
    return m as Map<boolean>;
  });
  const isAnyLoading = React.useMemo(
    () => Object.values(loadingByField).some(Boolean),
    [loadingByField],
  );

  useEffect(() => { loadingRef.current = loadingByField; }, [loadingByField]);

  const trySave = useCallback(
    (key: string) => {
      if (timeoutsRef.current[key]) {
        clearTimeout(timeoutsRef.current[key]);
        delete timeoutsRef.current[key];
      }
      if (loadingRef.current[key]) {
        return;
      }

      const field = fieldsRef.current.find(f => fieldKey(f) === key);
      if (!field) {
        return;
      }

      const desired   = valuesRef.current[key];
      const lastSaved = lastSavedRef.current[key] ?? (field.answer ?? '');
      if (desired === undefined || desired === lastSaved) {
        return;
      }

      lastSavedRef.current = R.assoc(key, desired, lastSavedRef.current);
      dispatch(updateDevPlanFieldAnswerAction(makeUpdateFieldAnswerRequestObj(field, desired)));
    },
    [dispatch],
  );

  // Seed lastSaved for new fields and reconcile when server values change while the user has no pending edits for that
  // field.
  useEffect(
    () => {
      let nextValues: Map<string> | null = null;
      props.fieldsAnswers.forEach(field => {
        const key         = fieldKey(field);
        const serverValue = field.answer ?? '';
        const localValue  = valuesRef.current[key];
        const lastSaved   = lastSavedRef.current[key];

        if (lastSaved === undefined) {
          lastSavedRef.current = R.assoc(key, serverValue, lastSavedRef.current);
          return;
        }

        const userIsDirty = localValue !== undefined && localValue !== lastSaved;
        if (serverValue !== lastSaved && !userIsDirty) {
          lastSavedRef.current = R.assoc(key, serverValue, lastSavedRef.current);
          if (localValue !== undefined && localValue !== serverValue) {
            nextValues = R.assoc(key, serverValue, nextValues ?? valuesRef.current);
          }
        }
      });
      if (nextValues) {
        setValues(nextValues);
      }
    },
    [props.fieldsAnswers],
  );

  // Per-field: when its save completes, if the user typed more during the in-flight window, save again.
  useEffect(
    () => {
      Object.keys(loadingByField).forEach(key => {
        if (wasLoadingRef.current[key] && !loadingByField[key]) {
          const desired   = valuesRef.current[key];
          const lastSaved = lastSavedRef.current[key];
          if (desired !== undefined && desired !== lastSaved) {
            trySave(key);
          }
        }
      });
      wasLoadingRef.current = loadingByField;
    },
    [loadingByField, trySave],
  );

  // Flush all pending debounced edits on unmount.
  useEffect(
    () => () => {
      Object.keys(timeoutsRef.current).forEach(key => {
        clearTimeout(timeoutsRef.current[key]);
        const field     = fieldsRef.current.find(f => fieldKey(f) === key);
        const desired   = valuesRef.current[key];
        const lastSaved = lastSavedRef.current[key];
        if (field && desired !== undefined && desired !== lastSaved && !loadingRef.current[key]) {
          lastSavedRef.current = R.assoc(key, desired, lastSavedRef.current);
          dispatch(updateDevPlanFieldAnswerAction(makeUpdateFieldAnswerRequestObj(field, desired)));
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onChange = useCallback(
    (key: string) => (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = event.target.value;
      setValues(prev => R.assoc(key, newValue, prev));

      if (timeoutsRef.current[key]) {
        clearTimeout(timeoutsRef.current[key]);
      }
      timeoutsRef.current[key] = setTimeout(() => trySave(key), SAVE_DEBOUNCE_MS);
    },
    [trySave],
  );

  const onBlur = useCallback(
    (key: string) => () => trySave(key),
    [trySave],
  );

  return (
    <div css={[styles.root, isAnyLoading && styles.loadingForm]} className={props.className}>
      {
        props.fieldsAnswers.map((item, i) => {
          if (item.list) {
            return (
              <DevActionListField
                key={item.question}
                field={item}
                devItemCompetencyId={props.devItemCompetencyId}
                placeholder={placeholder}
                disableModal={!props.isParticipant}
                css={[styles.inputGroup]}
              />
            );
          }

          const key   = fieldKey(item);
          const value = values[key] ?? item.answer ?? '';

          return (
            <DevActionField
              key={item.question}
              item={item}
              placeholder={placeholder}
              rows={calcLines(value)}
              value={value}
              onChangeAnswer={props.isParticipant ? onChange(key) : undefined}
              onBlurAnswer={props.isParticipant ? onBlur(key) : undefined}
              disableTextArea={!props.isParticipant}
              textAreaStyle={[TEXT_AREA, styles.textArea]}
              css={[
                styles.inputGroup,
                i === props.fieldsAnswers.length - 1 && css`margin-bottom: 0;`,
              ]}
            />
          );
        })
      }
    </div>
  );
};

export default DevActionForm;
